'use client'

import { FormSubmit, useFormContext } from '@ariakit/react'
import { ComponentProps, MouseEvent, useContext } from 'react'

import { Button } from '@syconium/little-miss-figgy'

import { ProductCatalogVariant } from '../../../__generated__/graphql/catalog/graphql'
import { trackEvent } from '../../../lib/analytics'
import { useFeatureFlag } from '../../_providers/ExperimentationProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import { FormSubmitContent } from '../form/FormSubmitContent'

import { VariantSelectionFormContext, VariantSelectionFormState } from './VariantSelectionForm'

type VariantSelectionFormPurchasingSubmitProps = {
  callToActionColor?: React.CSSProperties['color']
} & ComponentProps<typeof Button>

const actionableAvailabilities: Array<ProductCatalogVariant['availability']> = [
  'AVAILABLE',
  'WAIT_LISTING',
]

export const VariantSelectionFormPurchasingSubmit = ({
  isPrimaryCtaForPageView,
  callToActionColor,
  ...buttonProps
}: VariantSelectionFormPurchasingSubmitProps) => {
  const { selectedVariant, variantGroup, trackingCategory, loading } = useContext(
    VariantSelectionFormContext
  )!
  const translations = useTranslation()
  const formContext = useFormContext()
  const fit = formContext!.useValue<VariantSelectionFormState['fit']>('fit') ?? ''
  const size = formContext!.useValue<VariantSelectionFormState['size']>('size') ?? ''

  const addToBagCtaCopyTestDecision = useFeatureFlag<
    'atb-cta-copy-test-control' | 'atb-cta-copy-test-variant'
  >({
    key: 'atb-cta-copy-test',
    controlVariant: 'atb-cta-copy-test-control',
    ifAccessedPriorToDecisionInitialization: 'lock-pageview-to-controlVariant',
    skip: size.length > 0 || variantGroup.availability !== 'AVAILABLE' || !isPrimaryCtaForPageView,
  })

  const allVariantFieldsFilled = size.length > 0 && fit.length > 0
  const notFound = allVariantFieldsFilled && !selectedVariant
  const disabledAvailability =
    allVariantFieldsFilled &&
    selectedVariant?.availability &&
    !actionableAvailabilities.includes(selectedVariant.availability)
  const disabled = loading || notFound || disabledAvailability

  const addToBagContent = buttonProps.children ?? translations.detailsSection.addToBagCTA
  const incompleteFormContent =
    addToBagCtaCopyTestDecision === 'atb-cta-copy-test-variant' && isPrimaryCtaForPageView
      ? translations.detailsSection.selectSizeAddToBagButtonCTA
      : addToBagContent

  return (
    <FormSubmit
      render={htmlProps => (
        <>
          <Button
            {...trackEvent({
              action: 'click',
              category: trackingCategory,
              label: 'Add to Bag',
            })}
            disabled={disabled}
            block
            data-color={variantGroup?.color?.id}
            data-fit={selectedVariant?.fit?.id}
            data-size={selectedVariant?.size?.id}
            size='large'
            callToActionColor={callToActionColor}
            {...htmlProps}
            {...buttonProps}
            onClick={(event: MouseEvent) => {
              // Some times people portal this button. React context passes portals, DOM form submit defaults do not.
              // Because of this, we manually submit the form.
              event.preventDefault()
              formContext?.submit()
              return false
            }}
          >
            <FormSubmitContent
              isSubmitting={loading}
              submittingLabel={translations.magnolia.general.loading}
            >
              {!allVariantFieldsFilled && !disabled
                ? incompleteFormContent
                : notFound
                ? translations.detailsSection.soldOutAddToBagCTA
                : selectedVariant?.availability === 'WAIT_LISTING'
                ? translations.detailsSection.waitlistAddToBagCTA
                : selectedVariant?.availability === 'AVAILABLE'
                ? addToBagContent
                : translations.detailsSection.soldOutAddToBagCTA}
            </FormSubmitContent>
          </Button>
        </>
      )}
    />
  )
}
